import { useContext, useEffect } from "react";
import store2 from "store2";
import { baseUrl } from "./config";
import useWebSocket from "react-use-websocket";
import { useBrowserNotification } from "../sockets/useBrowserNotification";
import { CollectionsContext } from "../components/root/CollectionsProvider";
import { Collection } from "../types/collections.types";
import { useLogoutFromDevice } from "../sockets/useLogoutFromDevice";
import { useNotificationsHandler } from "../sockets/useNotificationsHandler";
import { useSessionAssistant } from "../components/assistant-dialog/useSessionAssistant";
import { KIOSK_CALLBACK_SUBSCRIPTION_UID, useKioskCallbackAssistant } from "../components/assistant-dialog/useKioskCallbackAssistant";
import { KIOSK_CALLBACK_TASK_KEY_PREFIX } from "../components/assistant-dialog/utils";
import { useLiveCalls } from "../sockets/useLiveCalls";
import { useSessionsRecordsInvalidate } from "../sockets/useSessionsRecordsInvalidate";
import { useGapTechsInvalidate } from "../sockets/useGapTechsInvalidate";
import { useUser } from "./useAuth";
import { ROLES, isLiveCallsRole } from "../utils/constants";

const socketUrl = `${baseUrl.replace(/^http/, "ws")}/websocket`;
export const useWebSockets = () => {
  const { createLeadNotification } = useBrowserNotification();
  const logOutFromDevice = useLogoutFromDevice();
  const { handleNotificationEvent } = useNotificationsHandler();
  const { handleSessionEvent } = useSessionAssistant();
  const { handleKioskCallbackEvent } = useKioskCallbackAssistant();
  const { handleLiveCallsEvent } = useLiveCalls();
  const { handleSessionsRecordsEvent } = useSessionsRecordsInvalidate();
  const { handleGapTechsEvent } = useGapTechsInvalidate();
  const collections = useContext(CollectionsContext);
  const { data: user } = useUser();
  const role = user?.role.id;

  const { sendMessage, lastMessage } = useWebSocket(socketUrl, {
    shouldReconnect: () => true,
    reconnectAttempts: Infinity,
    reconnectInterval: 3000,
    onOpen: () => {
      console.log("WebSocket Connected");
      const access_token = store2.get("auth")?.access_token;

      // Authenticate upon opening the connection
      sendMessage(
        JSON.stringify({
          type: "auth",
          access_token,
        })
      );
    },
    onError: (error) => console.error("WebSocket Error:", error),
    onClose: (event) => console.log("Disconnected:", event.code, event.reason),
  });


  type SubscriptionOptions = {
    uid?: string;
    filter?: Record<string, any>;
    skipInitialData?: boolean;
  };

  const createSubscription = async (collectionToSubscribe: string, fields: Array<string>, collections: Array<Collection>, options?: SubscriptionOptions) => {
    const collectionExists = collections.find((collection) => collection.collection === collectionToSubscribe);

    if (collectionExists) {
      const query: Record<string, any> = { fields };
      if (options?.filter) {
        query.filter = options.filter;
      }
      if (options?.skipInitialData) {
        query.limit = 0;
      }
      
      sendMessage(
        JSON.stringify({
          type: "subscribe",
          collection: collectionToSubscribe,
          query,
          uid: options?.uid ?? collectionToSubscribe,
        })
      );
    }
  };

 

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);

      if (data.type === "auth" && data.status === "ok") {
        if (!collections) return;
        createSubscription("leads", ["*.*"], collections.collections, { skipInitialData: true });
        createSubscription("call_center_licenses", ["*.*"], collections.collections, { skipInitialData: true });
        createSubscription("onpremises_devices_usage", ["*.*"], collections.collections, { skipInitialData: true });
        createSubscription(
          "notifications",
          ["id", "content", "type", "navigation_path", "user_that_generated_notification.first_name", "user_that_generated_notification.last_name"],
          collections.collections,
          {
            filter: { user_to_be_notified: { _eq: "$CURRENT_USER" } },
            skipInitialData: true,
          }
        );
        createSubscription(
          "sessions",
          ["id", "status", "start_date", "service", "technician.user_id", "treatment.id", "treatment.client.id", "treatment.client.client_id", "treatment.client.user_id.first_name", "treatment.client.user_id.last_name", "treatment.client.clinic_id.id", "treatment.client.clinic_id.receptionist"],
          collections.collections,
          { 
            filter: { 
              _and: [
                { status: { _in: ["confirmed", "awaiting_in_clinic", "in_session", "done", "missed", "cancelled", "staff_unavailable"] } },
                { service: { _nnull: true } },
              ],
            },
            skipInitialData: true 
          }
        );
        createSubscription(
          "tasks",
          ["id", "status"],
          collections.collections,
          { skipInitialData: true }
        );
        createSubscription(
          "tasks",
          ["id"],
          collections.collections,
          {
            uid: KIOSK_CALLBACK_SUBSCRIPTION_UID,
            filter: { hidden_field_automations: { _starts_with: KIOSK_CALLBACK_TASK_KEY_PREFIX } },
            skipInitialData: true,
          }
        );
        createSubscription(
          "live_calls",
          [
            "id", "session_id", "direction", "state", "agent_extension", "phone_number",
            "caller_name", "caller_collection", "caller_item_id",
            "started_at", "answered_at", "last_event_at",
            "agent_user.id", "agent_user.first_name", "agent_user.last_name", "agent_user.avatar"
          ],
          collections.collections,
          { skipInitialData: false }
        );
      }

    

      
      if (data.type === "subscription") {
        // Handle different collections subscriptions
        if (data.uid === "leads" || data.uid === "call_center_licenses") {
            if(role === ROLES.admin || role === ROLES.shilan) return;
          createLeadNotification(data);
        } else if (data.uid === "onpremises_devices_usage") {
          logOutFromDevice(data);
        } else if (data.uid === "notifications") {
            if(role === ROLES.admin || role === ROLES.shilan) return;
          handleNotificationEvent(data);
        } else if (data.uid === "sessions" || data.uid === "tasks") {
          if (data.uid === "sessions") {
            handleSessionsRecordsEvent(data);
            handleGapTechsEvent(data);
          }
          handleSessionEvent(data);
        } else if (data.uid === KIOSK_CALLBACK_SUBSCRIPTION_UID) {
          handleKioskCallbackEvent(data);
        } else if (data.uid === "live_calls") {
          if (!isLiveCallsRole(role)) return;
          handleLiveCallsEvent(data);
        }
      }

      if (data.type === "ping") {
        sendMessage(JSON.stringify({ type: "pong" }));
      }

      if (data.status === "error") {
        console.log('WebSocket error =>', data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, collections]);
};

export type SubscriptionData = {
  type: "subscription";
  event: "init" | "create" | "update" | "delete";
  uid: string;
  data: any;
};
